// Interruptor de reversión de la migración a Postgres (config/migration).
//
// Durante la migración cada dominio se lee de Firestore o de Postgres según
// ESTE documento. Revertir es escribir un valor en Firestore, no recompilar ni
// desplegar: un rollback que exige compilar no es un rollback.
//
// El interruptor vive en Firestore A PROPÓSITO, no en una variable del build ni
// en la propia Postgres. Si viviera en Postgres no se podría apagar cuando
// Postgres está caída, que es justo cuando hace falta.
//
// ─── HAY TRES LECTORES DE ESTE DOCUMENTO ────────────────────────────────────
//
//   1. api/src/types/migration.ts + api/src/lib/dualWrite.ts  ← LA FORMA CANÓNICA
//   2. este archivo                                            (la app React)
//   3. functions/src/migration.js                              (las Cloud Functions)
//
// Si no deciden EXACTAMENTE igual, un dominio queda leyéndose de dos sitios a la
// vez: el disparador escribe en Firestore mientras la pantalla lee de Postgres.
// Eso no sale en ningún log; sale como datos que no cuadran.
//
// ⚠️ CUALQUIER CAMBIO AQUÍ VA TAMBIÉN EN `functions/src/migration.js`, y los dos
// tienen que seguir al API. La guarda no es esta nota — las notas de "mantener
// en sincronía a mano" ya fallaron tres veces en este repositorio
// (ASSISTANT_TOOLS, isClientProUser, las tablas de intervalos). La guarda es
// `functions/tests/pure.migration.test.js`, que compila ESTE fuente y lo corre
// contra el mismo Firestore de mentira que al gemelo del backend, comparando
// ambos con una tabla derivada del zod real del API.
//
// ─── LA REGLA QUE MÁS FÁCIL SE PIERDE AL COPIAR ─────────────────────────────
//
// El documento se valida ENTERO. Un solo valor mal puesto en CUALQUIER dominio
// invalida el documento completo y TODO vuelve a 'firestore':
//
//   { version:1, domains:{ orders:{read:'Postgres'}, users:{read:'postgres'} } }
//        → `users` también se lee de FIRESTORE, aunque esté impecable
//
// Es lo que hace el `safeParse` de zod en el API. Un lector que saneara dominio
// por dominio dejaría `users` en Postgres mientras el API lo lee de Firestore.
// Duele un poco (un dedazo apaga la migración entera) pero falla SIEMPRE hacia
// el camino viejo, que es el seguro.
//
// Regla no negociable: sin documento, con lectura fallida, con un documento que
// no valida, con un valor raro o con un dominio no listado, la respuesta es
// 'firestore'. NUNCA lanza.
//
// ─── LOS CUATRO CAMPOS DE CADA DOMINIO, Y CUÁL NOS INCUMBE ──────────────────
//
// La app ACTÚA solo sobre `read` (y sobre `killSwitch`). Los otros tres se
// parsean, se validan y se exponen, pero no cambian lo que responde `readsFrom`:
//
// • `shadow` (escritura en sombra) y `mirrorBack` (espejo inverso) los ejecuta
//   el API. La app no escribe a Postgres por su cuenta, así que no hay nada que
//   decidir aquí. OJO: `read:'postgres'` con `shadow:false` es un estado ILEGAL,
//   pero quien lo denuncia es `validateMigrationConfig` del API al arrancar;
//   esta copia NO lo corrige al vuelo, porque "arreglarlo" resolviendo a
//   firestore la haría discrepar del API justo en el estado más delicado.
//
// • `writeCutover` marca que la escritura pasó a ir a Postgres primero. Hoy la
//   app escribe siempre por el mismo camino, así que se expone (vía
//   `snapshot.domain()`) y no se actúa. Se expone en vez de esconderse para que
//   nadie tenga que volver a parsear el documento en otro archivo, que es
//   exactamente como nacen los cuartos gemelos.
//
// Mismo patrón que paymentsConfig.js / launchConfig.js, con una diferencia
// deliberada: el caché CADUCA. Aquellos cachean de por vida porque su valor
// cambia una vez en la historia del producto; este cambia durante un corte y su
// reversión tiene que llegar a las pestañas ya abiertas sin pedirle a nadie que
// recargue.
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Las UNIDADES DE CORTE de la migración. No son las olas del plan: salen de las
// dependencias del código (ver db/MAPPING.md §9 y api/src/types/migration.ts).
// Esta lista y la de los otros dos lectores TIENEN que ser la misma.
export const MIGRATION_DOMAINS = [
  'audit',      // adminAuditLog, profileEvents, analytics/*, termsAcceptances, announcements
  'users',      // users + subcolecciones private/ y favorites/
  'vehicles',   // vehicles, maintenanceRecords, fuelLogs, aiDiagnoses, pendingCertifiedServices
  'workshops',  // workshops, appointments, reviews, sharedAccess, vehicleShares
  'catalog',    // products (+ private/pricing), productReviews
  'orders',     // orders, carts
  'listings',   // vehicleListings, vehicleInspections, supportTickets, suggestions
  'billing',    // billing, azulSessions, paymentEvents, aiTopUps
];

const DOMAIN_DEFAULT = { read: 'firestore', shadow: false, mirrorBack: false, writeCutover: false };

function deepFreeze(obj) {
  if (obj && typeof obj === 'object') {
    for (const v of Object.values(obj)) deepFreeze(v);
    Object.freeze(obj);
  }
  return obj;
}

function defaultConfig() {
  const domains = {};
  for (const d of MIGRATION_DOMAINS) domains[d] = { ...DOMAIN_DEFAULT };
  return { version: 1, killSwitch: false, domains };
}

// Estado inicial: TODO en Firestore. Es lo que se siembra en la Fase 0 y lo que
// se devuelve ante cualquier duda.
export const MIGRATION_CONFIG_DEFAULT = deepFreeze(defaultConfig());

// Ventana del caché. 30s: los MISMOS que el backend y el API, a propósito.
// Durante la ventana los tres lectores pueden discrepar, y esa ventana es el
// MÁXIMO de las tres, no la suma: acortar solo esta copia no reduce nada y
// multiplica las lecturas. Igualarlas acota el desacuerdo a 30s.
export const MIGRATION_CACHE_MS = 30000;

// ─── EL VALOR GUARDADO ENTRE ARRANQUES, Y POR QUÉ SU PLAZO ES OTRO ──────────
//
// El caché de arriba vive en MEMORIA, así que en un arranque en frío está vacío
// y la primera lectura de `config/migration` es un viaje a Firestore POR DELANTE
// de todo lo demás: nadie puede pedir sus datos sin saber antes de qué base los
// pide. Guardar el último valor conocido en el navegador quita ese eslabón.
//
// ⚠️ NO ES UN CACHÉ MÁS LARGO. El valor guardado se sirve UNA sola vez por
// sesión —antes de que se resuelva la primera lectura de red, y disparándola en
// el mismo acto— y a partir de ahí manda el caché de 30s de siempre. Si sirviera
// también después, revertir un dominio tardaría cinco minutos en llegar a una
// pestaña abierta en vez de treinta segundos, que es justo la propiedad que este
// módulo existe para conservar.
//
// EL PLAZO: 5 minutos. La defensa, en las dos direcciones:
//
// • Por qué no más: el interruptor es la escotilla de emergencia del dueño. Si
//   revierte `users` a Firestore porque Postgres está ardiendo, todo el que abra
//   la app en frío durante el plazo hace su PRIMERA carga contra la base caída
//   (Home toma una instantánea al empezar y la usa para toda esa carga). Media
//   hora de gente entrando a una pantalla rota no es un caché, es un incidente
//   más largo. Cinco minutos es el orden del ciclo de comprobación de quien está
//   tocando el interruptor: toca, recarga, mira.
// • Por qué no menos: por debajo de un par de minutos no ahorra nada. Lo que
//   aprovecha esto son las reaperturas seguidas —recargar tras un fallo, volver
//   a la PWA que iOS mató, abrir en otra pestaña—, no la visita de mañana.
// • Y por qué es tolerable ser 10× la ventana en memoria: el daño está acotado
//   al PRIMER pintado, y solo mientras la revalidación viaja (0.26-0.42 s desde
//   República Dominicana). No es "cinco minutos leyendo mal": es una carga.
export const MIGRATION_PERSIST_MS = 300000;

// Versión en la clave: si algún día cambia la FORMA de lo guardado, los valores
// viejos no se leen mal, sencillamente no se encuentran.
const MIGRATION_STORE_KEY = 'mekafix.migrationConfig.v1';

let cached = null;
let cachedAt = 0;
let inFlight = null;
// El valor guardado solo entra en juego antes de la primera lectura de red de
// esta sesión. Se cierra en cuanto una lectura resuelve (y cuando alguien tira
// el caché a mano: quien pide leer no quiere que le sirvan lo de antes).
let respaldoCerrado = false;
let respaldoLeido = false;
let respaldo = null;

// El almacén del navegador puede no existir (el arnés de paridad de
// `functions/tests` compila ESTE fuente y lo corre en Node) o lanzar al tocarlo
// (modo privado, políticas de empresa). En los dos casos el módulo se comporta
// como antes de este cambio: nada de esto puede tumbar el arranque.
function almacen() {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch (_) {
    return null;
  }
}

function leerGuardado() {
  if (respaldoLeido) return respaldo;
  respaldoLeido = true;
  try {
    const store = almacen();
    const crudo = store && store.getItem(MIGRATION_STORE_KEY);
    if (!crudo) return null;
    const guardado = JSON.parse(crudo);
    if (!isRecord(guardado)) return null;
    if (typeof guardado.at !== 'number' || !Number.isFinite(guardado.at)) return null;
    // Se valida con `parseOrNull`, el MISMO validador del documento y no otro:
    // un valor de otra versión del esquema, o adulterado a mano en la consola
    // del navegador, se descarta y se lee de la red como si no hubiera nada.
    const config = parseOrNull(guardado.config);
    if (!config) return null;
    respaldo = { config, at: guardado.at };
    return respaldo;
  } catch (_) {
    return null;      // JSON roto, almacén que lanza: se lee de la red y ya
  }
}

// Lo guardado, solo si sirve AHORA. Un `at` en el futuro (alguien movió el reloj
// del equipo) cuenta como caducado: no se sabe cuánto tiene.
function respaldoVigente() {
  if (respaldoCerrado) return null;
  const g = leerGuardado();
  if (!g) return null;
  const edad = Date.now() - g.at;
  if (edad < 0 || edad >= MIGRATION_PERSIST_MS) return null;
  return g.config;
}

function guardar(config) {
  try {
    const store = almacen();
    if (store) store.setItem(MIGRATION_STORE_KEY, JSON.stringify({ at: Date.now(), config }));
  } catch (_) { /* cuota llena o almacén bloqueado: no se guarda y no pasa nada */ }
}

function olvidarGuardado() {
  respaldo = null;
  respaldoLeido = true;
  try {
    const store = almacen();
    if (store) store.removeItem(MIGRATION_STORE_KEY);
  } catch (_) { /* ídem */ }
}

// ─── Parseo (misma lógica, campo a campo, que el gemelo del backend) ─────────

function isRecord(v) { return !!v && typeof v === 'object' && !Array.isArray(v); }
function isBoolOrAbsent(v) { return v === undefined || typeof v === 'boolean'; }

// Devuelve el dominio saneado, o null si NO valida. Null invalida el documento
// ENTERO: ver la nota de arriba.
function parseDomainConfig(raw) {
  if (!isRecord(raw)) return null;
  const read = raw.read === undefined ? 'firestore' : raw.read;
  if (read !== 'firestore' && read !== 'postgres') return null;
  if (!isBoolOrAbsent(raw.shadow)) return null;
  if (!isBoolOrAbsent(raw.mirrorBack)) return null;
  if (!isBoolOrAbsent(raw.writeCutover)) return null;
  return {
    read,
    shadow: raw.shadow === true,
    mirrorBack: raw.mirrorBack === true,
    writeCutover: raw.writeCutover === true,
  };
}

// Devuelve la configuración válida, o null si el documento no vale. Separado de
// `parseMigrationConfig` porque el lector necesita DISTINGUIR "no valida" de "no
// hay nada encendido": lo primero no se cachea, para que el arreglo llegue
// enseguida y no dentro de treinta segundos.
function parseOrNull(raw) {
  if (!isRecord(raw)) return null;
  if (raw.version !== 1) return null;                    // z.literal(1)
  if (!isBoolOrAbsent(raw.killSwitch)) return null;
  // `updatedAt` y `updatedBy` NO se validan A PROPÓSITO: son metadatos para
  // humanos y no llevan ni un bit de la decisión de qué base se lee.
  // Validarlos hacía que escribir la fecha con serverTimestamp() —el modo
  // idiomático de Firestore, y el tipo que la consola ofrece para un campo que
  // se llama así— invalidara el documento ENTERO y revirtiera los ocho dominios
  // en silencio. Y mordía en el peor momento: mientras alguien edita el
  // interruptor, o sea durante un paso de migración o una reversión.
  // Lo que SÍ decide algo (version, killSwitch, domains) se sigue validando
  // ENTERO: un solo dominio mal puesto invalida el documento completo.
  // ⚠️ Esta regla es IDÉNTICA en los tres lectores (aquí, functions/src/
  // migration.js y api/src/types/migration.ts). Relajar o endurecer uno solo
  // CREA el split-brain que esto existe para evitar. Hay una prueba de paridad
  // en api/tests/migration.parity.test.ts que lo impide.
  if (!isRecord(raw.domains)) return null;

  const domains = {};
  for (const name of Object.keys(raw.domains)) {
    const d = parseDomainConfig(raw.domains[name]);
    if (!d) return null;                                 // uno malo invalida todo
    domains[name] = d;
  }

  // El interruptor de pánico del dueño: todo vuelve a Firestore y se ignora el
  // resto del documento.
  if (raw.killSwitch === true) return { ...defaultConfig(), killSwitch: true };
  return { version: 1, killSwitch: false, domains };
}

// FALLA CERRADO HACIA LO CONOCIDO: si no valida, todo en Firestore.
export function parseMigrationConfig(raw) {
  return parseOrNull(raw) || defaultConfig();
}

// De dónde se LEE un dominio, a partir de una configuración ya parseada.
export function readSourceOf(cfg, domain) {
  if (!cfg || cfg.killSwitch) return 'firestore';
  const d = cfg.domains && cfg.domains[domain];
  return isRecord(d) && d.read === 'postgres' ? 'postgres' : 'firestore';
}

// Los cuatro campos de un dominio. Uno ausente del documento sale con los
// defaults, nunca undefined.
export function domainConfigOf(cfg, domain) {
  if (!cfg || cfg.killSwitch) return { ...DOMAIN_DEFAULT };
  const d = cfg.domains && cfg.domains[domain];
  if (!isRecord(d)) return { ...DOMAIN_DEFAULT };
  return {
    read: d.read === 'postgres' ? 'postgres' : 'firestore',
    shadow: d.shadow === true,
    mirrorBack: d.mirrorBack === true,
    writeCutover: d.writeCutover === true,
  };
}

// ─── Lectura ────────────────────────────────────────────────────────────────

export async function loadMigrationConfig() {
  if (cached && Date.now() - cachedAt < MIGRATION_CACHE_MS) return cached;

  // Arranque en frío con valor guardado: se responde YA y la lectura de red sale
  // igual, solo que sin nadie esperándola. Todo el que llame mientras viaja
  // recibe el MISMO valor (el almacén no cambia debajo), así que no hay media
  // pantalla leyendo de una base y media de la otra.
  const guardado = respaldoVigente();
  if (guardado) {
    lecturaDeRed();
    return guardado;
  }

  return lecturaDeRed();
}

function lecturaDeRed() {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const snap = await getDoc(doc(db, 'config', 'migration'));

      // El documento todavía NO existe en producción, y ese es el estado normal
      // de la Fase 0: se cachea como cualquier otra respuesta buena.
      if (!snap.exists()) {
        cached = defaultConfig();
        cachedAt = Date.now();
        guardar(cached);
        return cached;
      }

      const value = parseOrNull(snap.data());
      // Un documento que existe pero no valida NO se cachea: el arreglo tiene
      // que llegar enseguida. (Aquí no se avisa a Sentry, a diferencia del
      // backend: en el navegador el usuario recarga y ve el cambio, y este
      // módulo se mantiene sin dependencias a propósito. Quien vigila que el
      // documento sea válido es el backend, que es donde no hay nadie mirando.)
      //
      // Y se BORRA lo guardado: un documento que hoy no vale es la prueba de que
      // lo que hay escrito ya no autoriza a leer de Postgres. Dejarlo pondría al
      // próximo arranque a servir un 'postgres' viejo mientras el API —que
      // valida igual y también falla cerrado— está leyendo de Firestore. Eso es
      // exactamente el desacuerdo entre lectores que este archivo evita.
      if (!value) { olvidarGuardado(); return defaultConfig(); }

      cached = value;
      cachedAt = Date.now();
      guardar(value);
      return value;
    } catch (e) {
      // Un fallo de lectura NO se cachea: se responde el camino seguro y la
      // siguiente llamada vuelve a intentar. Lo guardado se deja INTACTO: una
      // lectura que no llegó no dice nada sobre lo que pone el documento, y
      // borrarlo por estar sin conexión un momento tira el arranque rápido sin
      // ganar ninguna seguridad.
      return defaultConfig();
    } finally {
      inFlight = null;
      // A partir de aquí esta sesión ya tiene canal propio: manda el caché de
      // 30s y el valor guardado no se vuelve a servir.
      respaldoCerrado = true;
    }
  })();

  return inFlight;
}

// De dónde se lee un dominio. Siempre resuelve a 'firestore' o 'postgres'.
export async function readsFrom(domain) {
  return readSourceOf(await loadMigrationConfig(), domain);
}

// Tira el caché para que la próxima lectura vuelva a preguntar. Hoy no la
// necesita nadie (el documento se cambia por consola, no desde la app); existe
// para que el día que haya un panel del interruptor la respuesta ya esté
// escrita, en vez de que alguien vuelva a tocar el caché desde fuera.
export function invalidateMigrationConfigCache() {
  cached = null;
  cachedAt = 0;
  // También se cierra el camino del valor guardado. Quien tira el caché está
  // pidiendo leer, no que le sirvan lo de antes por otra puerta.
  respaldoCerrado = true;
}

// Instantánea para trabajo largo: una pantalla que pagina una lista no puede
// cambiar de base entre la página 1 y la 2, igual que un cron no puede cambiar a
// mitad de corrida. Lo que devuelve está CONGELADO hasta el fondo y su
// `readsFrom` es SÍNCRONO, así que dentro del bucle no hay nada que pueda releer
// sin querer. Esa es la propiedad que importa, y es la que se conserva.
//
// Lo que NO hace es tirar el caché para leer fresco. Lo hacía, y con ello
// destruía en cada llamada el caché de 30s que comparte con `readsFrom()`: hay
// 158 llamadas repartidas por la app, varias en camino caliente (una POR CITA
// dentro de un `.map`, una por cada navegación), así que abrir /home costaba
// ~11 lecturas del MISMO documento. La frescura en el instante t0 se cambia por
// "como mucho 30s de antigüedad", que es EXACTAMENTE lo que `readsFrom()` ya
// acepta hoy y la misma ventana que usan a propósito el backend y el API (ver
// la nota de MIGRATION_CACHE_MS): acortar solo la copia del front no reduce el
// desacuerdo global ni un segundo, porque el API sigue con su ventana de 30s.
//
// `{ fresh: true }` es la escotilla para quien necesite leer sin esperar a que
// caduque. Hoy no la usa nadie.
export async function snapshotMigrationConfig({ fresh = false } = {}) {
  if (fresh) invalidateMigrationConfigCache();
  const cfg = await loadMigrationConfig();
  // Volver a parsear hace de copia profunda (para no congelar el objeto del
  // caché, que se comparte) y de paso deja la instantánea en forma canónica.
  const config = deepFreeze(parseMigrationConfig(cfg));
  return Object.freeze({
    at: Date.now(),
    config,
    readsFrom: (domain) => readSourceOf(config, domain),
    domain: (name) => Object.freeze(domainConfigOf(config, name)),
  });
}
